import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { OnboardIndividualRequest } from '../../services/metadata-service/metadata-service.type';
import { FormStage } from '../../services/multipart-holder/multipart-form-holder.service';
import { OnboardIndividualFinishedFormStructure } from './form.types';

export function patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // test the value of the control against the regexp supplied
    const valid = regex.test(control.value);

    // if true, return no error (no error), else return error passed in the second parameter
    return valid ? null : error;
  };
}

export function transformFormControlsToOnboardCall(
  form: OnboardIndividualFinishedFormStructure,
): OnboardIndividualRequest {
  const request = {
    firstName: form.account?.name.firstName,
    middleName: form.account.name.middleName,
    lastName: form.account.name.lastName,
    dob: form.account.dob.toISOString().split('T')[0],
    email: form.account.email,
    phoneNumber: form.account.dialCode + form.account.phone,
    password: form.account.password,
    country: form.address.country,
    address1: form.address.line_1,
    address2: form.address.line_2,
    city: form.address.city,
    county: form.address.county,
    postalCode: form.address.post_code,
    extraAttributes: form.additional,
  };
  return request;
}

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password: string = control.get('password')?.value; // get password from our password form control
  const confirmPassword: string = control.get('confirmPassword')?.value; // get password from our confirmPassword form control
  return password === confirmPassword ? null : { noPassswordMatch: true };
};

export const onboardIndividualFormStages = [
  new FormStage({
    position: 0,
    name: 'account',
    route: '/onboard-individual/account',
    form: new FormGroup(
      {
        name: new FormGroup({
          firstName: new FormControl('', Validators.required),
          middleName: new FormControl(''),
          lastName: new FormControl('', Validators.required),
        }),
        dob: new FormControl('', Validators.required),
        email: new FormControl('', [Validators.email, Validators.required]),
        phone: new FormControl('', Validators.required),
        dialCode: new FormControl('+44', Validators.required),
        phoneVerified: new FormControl(false, [Validators.required, Validators.requiredTrue]),
        password: new FormControl(
          '',
          Validators.compose([
            Validators.required,
            patternValidator(/\d/, { hasNumber: true }),
            patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            patternValidator(/[a-z]/, { hasSmallCase: true }),
            patternValidator(/[^\p{L}\p{N}]/u, {
              hasSpecialCharacters: true,
            }),
            Validators.minLength(8),
          ]),
        ),
        confirmPassword: new FormControl('', Validators.compose([Validators.required])),
      },
      { validators: [passwordMatchValidator] },
    ),
  }),
  new FormStage({
    position: 1,
    name: 'address',
    route: '/onboard-individual/address',
    form: new FormGroup({
      country: new FormControl('', Validators.required),
      line_1: new FormControl('', Validators.required),
      line_2: new FormControl(''),
      city: new FormControl('', Validators.required),
      county: new FormControl(''),
      post_code: new FormControl('', Validators.required),
    }),
  }),
];

export { phoneNumberValidator, phoneWithCodeValidator } from '@finxone-platform/shared/frontend';

export function minAgeValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null; // if empty, let required handle it

    const today = new Date();
    const dob = new Date(value);
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();

    // adjust if birthday hasn't occurred yet this year
    const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    return actualAge < minAge ? { minAge: { requiredAge: minAge, actualAge } } : null;
  };
}
